























































































































































































































































































































































































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Mobile } from "@/mixins/mobile";
import RadioButtonBox from "@/components/elements/utility/RadioButtonBox.vue";
import Dropdown from "@/components/elements/utility/Dropdown.vue";
import DropdownFinishes from "@/components/elements/utility/DropdownFinishes.vue";
import ProductService from "@/services/product_service";
import { AuthError } from "@/services/error_service";
import { NotificationService } from "@/services/notification_service";
import { BaseAccessory, BaseModel } from "@/models/products/base";
import { Collection, ProductLineName } from "@/models/products/collection";
import {
  ProductType,
  SingleProductRes,
  SingleProductWithBasesRes
} from "@/models/products/product";
import { UmbrellaType } from "@/models/products/umbrella";
import {
  Finish,
  FinishDisplayName,
  FinishHandle
} from "@/models/products/finish";
import { FinishPrices } from "@/models/pricing/price";
import { Stem } from "@/models/configurator/configurator_options";
import {
  BaseSelection,
  BaseSelectionChange,
  BaseSelectionOptions,
  SPIGOT_STEM
} from "@/models/configurator/base_selection";
import { baseStemNotes } from "@/utility/shared_design_lines";
import { getBaseFinishImage } from "@/utility/helpers";

/** The "No Base" tile's radio value. Not a model code, deliberately. */
const NO_BASE = "__no_base__";

/**
 * Bases the catalog still returns but that must not be orderable, ported from
 * Shade Designer's BaseSidebar. These are correctness rules, not polish: the
 * first five have no current product behind them and the three stems are a
 * client decision to sell them only as a base's mount.
 */
const HIDDEN_BASES = [
  "50C",
  "NGU550-E",
  "BZ",
  "XB",
  "XB+C",
  "8ST",
  "18ST",
  "18ST2"
];
/** 480 has no market-umbrella variant yet. */
const HIDDEN_BASES_MARKET = ["480"];
/** Base product lines that are never offered in the configurator. */
const HIDDEN_BASE_LINES = ["specialty", "in_ground_direct_mount"];
/** Catalina is sold with four galvanized plates only. */
const CATALINA_LINE = "galvanized_steel_plate";
const CATALINA_BASES = ["20G-SQ", "24G-SQ", "24G", "30G"];

const STEM_ORDER: Stem[] = [Stem.Stem8ST, Stem.Stem18ST, Stem.Stem18ST2];

/**
 * The card sits beside the umbrella's own repricing, and a rep clicking
 * through stems and finishes would otherwise interleave responses.
 */
const PRICE_DEBOUNCE_MS = 250;

/** The order every swatch row uses: the FinishHandle declaration order. */
const FINISH_ORDER: string[] = (Object.values(FinishHandle) as string[]).filter(
  // FinishHandle's members are written `"ms" as any`, which makes TypeScript
  // emit a reverse mapping, so Object.values yields handles and KEYS
  // interleaved. Handles are lowercase; keys are not.
  value => typeof value === "string" && value === value.toLowerCase()
);

interface BaseGroup {
  handle: string;
  display_name: string;
  models: BaseModel[];
}

/** A base as the full catalog knows it, plus where it sits in that catalog. */
interface CatalogEntry {
  doc: BaseModel;
  order: number;
  line: string;
}

/**
 * FUPOF-193 — choose a base, and its cover / wheels / stem / finishes, on the
 * umbrella page.
 *
 * Renders as the inner content of an AccordionSection the parent supplies; it
 * owns no card chrome of its own.
 *
 * The behaviour rules are Shade Designer's `/designer/base`
 * (`tt-shade-designer/src/components/designer/sidebar/base/BaseSidebar.vue`),
 * with three deliberate differences:
 *
 * - a **No Base** tile instead of a "clear base" link;
 * - the base finish and the cover finish are held in **separate** fields. SD
 *   funnels both through one `selectBase(model, finish, …)` argument and tests
 *   membership with `Array.includes`, which is reference identity — so picking
 *   a cover swatch silently resets the base finish to its default;
 * - no synchronous `verifyImageUrl()` HEAD request per tile.
 *
 * State lives here rather than on the parent's `item_details` on purpose:
 * `configFingerprint` stringifies everything on `item_details`, and a ~12KB
 * BaseModel landing there (with its `updated_at`) would retire the shared
 * design pill on an untouched design the moment this component resolved.
 */
@Component({
  components: {
    RadioButtonBox,
    Dropdown,
    DropdownFinishes
  }
})
export default class BaseSelector extends Mixins(Mobile) {
  /** The umbrella model the base has to fit, e.g. "880ECU". */
  @Prop({ type: String, default: "" }) umbrellaModel!: string;
  @Prop({ type: String, default: null }) handle!: ProductLineName;
  /**
   * The product line's `specific_mounting`. Branch on `=== true`: the field is
   * optional on Collection, and `undefined` must not fall into the curated
   * branch by accident.
   */
  @Prop({ type: Boolean, default: false }) specificMounting!: boolean;
  /**
   * The product line's `umbrella_type`, needed only for the market hide rule.
   * Optional so the card still renders sensibly if it isn't supplied.
   */
  @Prop({ type: String, default: null }) umbrellaType!: UmbrellaType | null;
  /** A shared design's base to start from. May arrive after this component. */
  @Prop({ type: Object, default: null }) preselect!: BaseSelection | null;

  protected NO_BASE = NO_BASE;
  protected productService = new ProductService();

  protected groups: BaseGroup[] = [];
  protected loading = true;
  /** Set when the catalog call itself failed, to avoid a second toast. */
  private loadError = false;

  protected selectedBase: BaseModel | null = null;
  protected baseFinish: Finish | null = null;
  protected baseStem: Stem | typeof SPIGOT_STEM | null = null;
  protected wheels = false;
  protected coverOn = false;
  /**
   * Held whether or not a cover is on, so switching base while a cover is on
   * carries the chosen finish over and only swaps the cover model. Defaults to
   * Golden Oak, as Shade Designer does.
   */
  protected coverFinish: Finish = {
    handle: FinishHandle.WG,
    display_name: FinishDisplayName.WG
  };

  protected basePrice = 0;
  protected coverPrice = 0;

  /** model -> catalog document, the hydration source for both branches. */
  private catalogByModel: Map<string, CatalogEntry> = new Map();
  private catalogById: Map<string, CatalogEntry> = new Map();
  /** cover model -> its per-finish upcharges. A cover's own msrp is 0. */
  private coverPrices: { [model: string]: FinishPrices[] } = {};

  /**
   * Set while the catalog or a preselect is being applied. Populating the
   * selection touches every field, so without this each one would queue its
   * own price request against a config that is still half-built.
   */
  private pricingSuspended = false;
  private priceTimer: number | null = null;
  /** Last request wins: a reply from an earlier sequence is dropped. */
  private priceSeq = 0;
  /** Price requests still out, stale ones included — see pricingBusy. */
  private pricingRequests = 0;
  /**
   * The price sequence number of the preselect's own pricing. Its reply is
   * the only one tagged `fromPreselect`: if the rep changes anything first,
   * the sequence moves on and that reply is dropped, so a rep change can never
   * be passed off as the preselect settling.
   */
  private preselectSeq: number | null = null;
  /** A preselect that arrived before the catalog did. */
  private pendingPreselect: BaseSelection | null = null;

  async created(): Promise<void> {
    this.pendingPreselect = this.preselect || null;
    await this.loadCatalog();
    this.applyPendingPreselect();
  }

  beforeDestroy(): void {
    this.cancelPendingPricing();
  }

  /**
   * A price the parent doesn't have yet: one is debounced, or one is in flight.
   * The debounce counts too — until it fires, the parent still holds the
   * previous selection while the tiles already show the new one.
   *
   * Counted rather than tied to the latest sequence number, so it always
   * returns to false: every request decrements in a `finally`, including a
   * superseded one whose reply is dropped, and including one that fails.
   *
   * The catalog load counts too. After the rep changes the umbrella model the
   * card refetches its bases, and until that lands the parent still holds a
   * base the new model may not accept — a submit then would pair the two.
   */
  protected get pricingBusy(): boolean {
    return this.loading || this.priceTimer !== null || this.pricingRequests > 0;
  }

  /**
   * Tells the parent whether a submit now would save a selection and price
   * that are about to change (FUPOF-193: the umbrella page holds its footer
   * until this settles).
   */
  @Watch("pricingBusy")
  private onPricingBusyChange(busy: boolean): void {
    this.$emit("busy", busy);
  }

  /**
   * The parent may resolve a shared design after this card has mounted, so a
   * preselect that arrives late has to be honoured too — otherwise the
   * design's base is silently dropped.
   */
  @Watch("preselect")
  private onPreselectChange(value: BaseSelection | null): void {
    if (!value) return;
    this.pendingPreselect = value;
    this.applyPendingPreselect();
  }

  /**
   * A different shape or size changes which bases fit. Re-fetch, then either
   * re-point the selection at its document in the new list or clear it.
   */
  @Watch("umbrellaModel")
  private async onUmbrellaModelChange(): Promise<void> {
    const previous = this.selectedBase;
    this.cancelPendingPricing();
    this.pricingSuspended = true;
    try {
      await this.loadCatalog();
      if (previous) {
        const doc = this.findModel(previous.model);
        if (doc) {
          this.applyBase(doc);
        } else {
          this.clearSelection();
        }
      }
    } finally {
      this.pricingSuspended = false;
    }
    // A preselect that landed while the refetch was in flight was deferred by
    // applyPendingPreselect's loading guard; it takes precedence.
    if (this.pendingPreselect) {
      this.applyPendingPreselect();
      return;
    }
    if (!previous) return;
    if (this.selectedBase) {
      this.scheduleRefreshPricing();
    } else {
      NotificationService.show(
        `${previous.display_model || previous.model} is not available for ` +
          `${this.umbrellaModel}. Please choose another base.`,
        "warning",
        12000
      );
      this.basePrice = 0;
      this.coverPrice = 0;
      this.emitChange();
    }
  }

  /* ----------------------------- catalog ----------------------------- */

  /**
   * Two calls, in parallel:
   *
   * 1. the full base catalog, grouped by base product line. This is the
   *    **hydration source** — it is the only route that returns bases with
   *    both `mounts` and `accessories` populated.
   * 2. for curated lines only, `/products/:umbrellaModel?bases`, which is the
   *    authoritative compatibility list. Its bases come back with `mounts: []`
   *    and a trimmed `finishes`, so it is read for WHICH models are offered
   *    and nothing else; each model's document comes from call 1.
   */
  private async loadCatalog(): Promise<void> {
    this.loading = true;
    try {
      const catalogReq = this.productService.getProducts({
        category: ProductType.Base,
        sort: "productline"
      });
      const compatReq =
        this.specificMounting === true && this.umbrellaModel
          ? this.productService.getSingleProduct(this.umbrellaModel, true)
          : null;
      const [catalogRes, compatRes] = await Promise.all([
        catalogReq,
        compatReq
      ]);

      const collections = (catalogRes || []) as Collection[];
      this.indexCatalog(collections);

      let groups: BaseGroup[];
      if (this.specificMounting === true) {
        const bases = compatRes
          ? ((compatRes as SingleProductWithBasesRes).bases || [])
          : [];
        groups = this.buildCompatibleGroup(bases);
      } else {
        groups = this.buildCatalogGroups(collections);
      }
      this.groups = groups.map(group => ({
        handle: group.handle,
        display_name: group.display_name,
        models: group.models.map(model => this.withDisplayOverride(model))
      }));
      this.loadError = false;
    } catch (err) {
      // getProducts / getSingleProduct have no 401 branch — a 401 arrives as a
      // generic APIError — so there is no AuthError to catch here.
      this.groups = [];
      this.loadError = true;
      NotificationService.show(
        "Unable to load base options. This umbrella can still be added " +
          "to the quote without a base.",
        "warning",
        12000
      );
    } finally {
      this.loading = false;
    }
  }

  /**
   * Index the catalog by model and by id, keeping each base's position so the
   * curated branch can be ordered by base product line rather than by the
   * compatibility list's own order.
   */
  private indexCatalog(collections: Collection[]): void {
    this.catalogByModel = new Map();
    this.catalogById = new Map();
    let order = 0;
    collections
      .filter(line => line.type === ProductType.Base)
      .forEach(line => {
        ((line.models || []) as BaseModel[]).forEach(doc => {
          const entry: CatalogEntry = {
            doc,
            order: order++,
            line: String(line.handle)
          };
          if (!this.catalogByModel.has(doc.model)) {
            this.catalogByModel.set(doc.model, entry);
          }
          if (doc._id && !this.catalogById.has(doc._id)) {
            this.catalogById.set(doc._id, entry);
          }
        });
      });
  }

  /** Curated lines: one group, in catalog order. */
  private buildCompatibleGroup(bases: BaseModel[]): BaseGroup[] {
    const resolved: { doc: BaseModel; order: number }[] = [];
    bases.forEach((thin, index) => {
      const entry =
        this.catalogByModel.get(thin.model) ||
        (thin._id ? this.catalogById.get(thin._id) : undefined);
      // Merge, rather than taking either document whole. The catalog has the
      // `mounts` and `accessories` the compatibility route omits, but the
      // compatibility route's `finishes` are scoped to THIS product line and
      // the catalog's are not — `DP` offers only Stainless under Eclipse and
      // three finishes under Aurora. Taking the catalog's list wholesale
      // offered finishes this umbrella cannot have.
      const doc = entry
        ? {
            ...entry.doc,
            finishes: thin.finishes?.length ? thin.finishes : entry.doc.finishes
          }
        : thin;
      if (this.isHiddenBase(doc, entry ? entry.line : "")) return;
      resolved.push({
        doc,
        order: entry ? entry.order : this.catalogByModel.size + index
      });
    });
    resolved.sort((a, b) => a.order - b.order);
    if (!resolved.length) return [];
    return [
      {
        handle: String(this.handle || "compatible"),
        display_name: "Bases & Mounts",
        models: resolved.map(item => item.doc)
      }
    ];
  }

  /** Every other line: the catalog's own groups, filtered. */
  private buildCatalogGroups(collections: Collection[]): BaseGroup[] {
    const groups: BaseGroup[] = [];
    collections.forEach(line => {
      const handle = String(line.handle);
      if (line.type !== ProductType.Base) return;
      if (HIDDEN_BASE_LINES.indexOf(handle) > -1) return;
      const models = ((line.models || []) as BaseModel[]).filter(
        doc => !doc.is_productline_specific && !this.isHiddenBase(doc, handle)
      );
      if (!models.length) return;
      groups.push({ handle, display_name: line.display_name, models });
    });
    return groups;
  }

  private isHiddenBase(model: BaseModel, line: string): boolean {
    if (HIDDEN_BASES.indexOf(model.model) > -1) return true;
    if (
      this.umbrellaType === UmbrellaType.Market &&
      HIDDEN_BASES_MARKET.indexOf(model.model) > -1
    ) {
      return true;
    }
    if (
      this.handle === ProductLineName.Catalina &&
      line === CATALINA_LINE &&
      CATALINA_BASES.indexOf(model.model) === -1
    ) {
      return true;
    }
    return false;
  }

  /**
   * Eclipse shows its DP base as "DP-SS". A client-side label only — the
   * catalog has no field for it — so the model code the API sees is untouched.
   */
  private withDisplayOverride(model: BaseModel): BaseModel {
    if (this.handle === ProductLineName.Eclipse && model.model === "DP") {
      return { ...model, display_model: "DP-SS" };
    }
    return model;
  }

  private findModel(model: string): BaseModel | null {
    for (const group of this.groups) {
      const match = group.models.find(doc => doc.model === model);
      if (match) return match;
    }
    return null;
  }

  /* ---------------------------- preselect ---------------------------- */

  private applyPendingPreselect(): void {
    const wanted = this.pendingPreselect;
    if (!wanted || this.loading) return;
    this.pendingPreselect = null;
    this.pricingSuspended = true;
    try {
      this.selectFromPreselect(wanted);
    } finally {
      this.pricingSuspended = false;
    }
    if (this.selectedBase) {
      // A preselect is one settled config, not a sequence of clicks, so price
      // it at once rather than through the debounce.
      this.refreshPricing(true);
    }
  }

  private selectFromPreselect(wanted: BaseSelection): void {
    const model = wanted.base ? this.findModel(wanted.base.model) : null;
    if (!model) {
      this.clearSelection();
      if (wanted.base && !this.loadError) {
        NotificationService.show(
          `Base ${wanted.base.model} is not available for this umbrella and ` +
            "was not selected.",
          "warning",
          12000
        );
      }
      this.basePrice = 0;
      this.coverPrice = 0;
      // The preselect settling to nothing — still the preselect, not the rep.
      this.emitChange(true);
      return;
    }
    this.selectedBase = model;

    const finishes = this.ownFinishes(model);
    const wantedHandle = wanted.baseFinish
      ? String(wanted.baseFinish.handle)
      : "";
    this.baseFinish =
      finishes.find(finish => String(finish.handle) === wantedHandle) ||
      finishes[0] ||
      null;

    // stemOptions, not mountStems: on a cantilever the only attachment is the
    // spigot, and reading the raw mounts here would preselect a stem the rep
    // is not offered and cannot change.
    const stems = this.stemOptions(model);
    this.baseStem =
      wanted.baseStem && stems.indexOf(wanted.baseStem) > -1
        ? wanted.baseStem
        : stems.length
        ? stems[0]
        : null;

    const options = wanted.baseOptions || {};
    this.wheels = this.hasWheels(model) && !!options.wheels;

    const accessory = this.coverAccessory(model);
    this.coverOn = !!(accessory && options.topCover);
    if (accessory && options.topCover) {
      this.coverFinish = this.validCoverFinish(
        accessory,
        options.topCover.finish
      );
    }
  }

  /* ---------------------------- selection ---------------------------- */

  protected get selectedValue(): string {
    return this.selectedBase ? this.selectedBase.model : NO_BASE;
  }

  /**
   * More than one base product line is on screen, each under its own heading.
   *
   * That is the non-specialty-mounting case in practice — a curated line
   * (`specific_mounting`) always resolves to a single group. It is keyed on
   * what is actually rendered rather than on the prop, because the layout
   * problem it solves only exists when there are headings for "No Base" to be
   * mistaken as sitting under.
   */
  protected get groupedLayout(): boolean {
    return this.groups.length > 1;
  }

  protected get selectedLabel(): string {
    if (!this.selectedBase) return "No Base";
    return this.selectedBase.display_model || this.selectedBase.model;
  }

  protected isSelected(model: BaseModel): boolean {
    return !!this.selectedBase && this.selectedBase.model === model.model;
  }

  /**
   * The selection as the parent consumes it. `baseOptions` carries a key only
   * when the option is chosen — `{}` rather than `{ wheels: false }` — which
   * is what Shade Designer writes and what the pricing endpoint expects.
   */
  protected get currentSelection(): BaseSelection | null {
    const base = this.selectedBase;
    if (!base) return null;
    const baseOptions: BaseSelectionOptions = {};
    if (this.wheels) baseOptions.wheels = true;
    const accessory = this.coverAccessory(base);
    if (this.coverOn && accessory) {
      baseOptions.topCover = {
        model: accessory.model,
        display_name: accessory.display_name,
        finish: this.coverFinish
      };
    }
    return {
      base,
      baseFinish:
        this.baseFinish ||
        (({ handle: null, display_name: null } as unknown) as Finish),
      baseStem: this.baseStem,
      baseOptions
    };
  }

  protected selectBase(model: BaseModel): void {
    if (this.isSelected(model)) return;
    this.applyBase(model);
    this.scheduleRefreshPricing();
  }

  protected clearBase(): void {
    this.cancelPendingPricing();
    // Drop any reply still in flight: it belongs to a base that is gone.
    this.priceSeq++;
    this.clearSelection();
    this.basePrice = 0;
    this.coverPrice = 0;
    this.emitChange();
  }

  private clearSelection(): void {
    this.selectedBase = null;
    this.baseFinish = null;
    this.baseStem = null;
    this.wheels = false;
    this.coverOn = false;
  }

  /**
   * Point the selection at a base and carry over what the new one still
   * offers — the finish and stem by handle, the cover by whether a -TC
   * accessory exists. Matching by handle rather than by object identity is the
   * whole of the SD carry-over bug fix.
   */
  private applyBase(model: BaseModel): void {
    const previousFinish = this.baseFinish;
    const previousStem = this.baseStem;
    this.selectedBase = model;

    const finishes = this.ownFinishes(model);
    const carried = previousFinish
      ? finishes.find(
          finish => String(finish.handle) === String(previousFinish.handle)
        )
      : undefined;
    this.baseFinish = carried || finishes[0] || null;

    const stems = this.stemOptions(model);
    // Carry the rep's stem across a base switch when the new base offers it;
    // otherwise take that base's first, which on a cantilever is the spigot.
    this.baseStem =
      previousStem && stems.indexOf(previousStem) > -1
        ? previousStem
        : stems.length
        ? stems[0]
        : null;

    if (!this.hasWheels(model)) this.wheels = false;

    const accessory = this.coverAccessory(model);
    if (!accessory) {
      this.coverOn = false;
    } else if (this.coverOn) {
      this.coverFinish = this.validCoverFinish(accessory, this.coverFinish);
    }
  }

  protected pickStem(model: BaseModel, stem: Stem | string): void {
    if (!this.isSelected(model)) this.applyBase(model);
    this.baseStem = stem as Stem | typeof SPIGOT_STEM;
    this.scheduleRefreshPricing();
  }

  protected pickWheels(model: BaseModel, value: boolean): void {
    if (!this.isSelected(model)) this.applyBase(model);
    this.wheels = value;
    this.scheduleRefreshPricing();
  }

  protected pickCover(model: BaseModel, value: boolean): void {
    if (!this.isSelected(model)) this.applyBase(model);
    const accessory = this.coverAccessory(model);
    this.coverOn = value && !!accessory;
    if (this.coverOn && accessory) {
      this.coverFinish = this.validCoverFinish(accessory, this.coverFinish);
    }
    this.scheduleRefreshPricing();
  }

  /**
   * The base's own finish. `model` is passed on desktop, where every tile
   * shows its own swatch row and clicking one also selects that base; mobile
   * passes only the handle, for the selected base.
   */
  protected pickBaseFinish(handle: FinishHandle, model?: BaseModel): void {
    const target = model || this.selectedBase;
    if (!target) return;
    if (!this.isSelected(target)) this.applyBase(target);
    const finish = this.ownFinishes(target).find(
      item => String(item.handle) === String(handle)
    );
    if (!finish) return;
    this.baseFinish = finish;
    this.scheduleRefreshPricing();
  }

  /**
   * The top cover's finish — and nothing else. Shade Designer routes this
   * through the same argument as the base finish, which resets the base finish
   * to its default on every cover swatch click.
   */
  protected pickCoverFinish(handle: FinishHandle): void {
    const base = this.selectedBase;
    if (!base) return;
    const accessory = this.coverAccessory(base);
    if (!accessory) return;
    const finish = (accessory.finishes || []).find(
      item => String(item.handle) === String(handle)
    );
    if (!finish) return;
    this.coverFinish = finish;
    this.coverOn = true;
    this.scheduleRefreshPricing();
  }

  protected mobilePickBase(value: string): void {
    if (value === NO_BASE) {
      this.clearBase();
      return;
    }
    const model = this.findModel(value);
    if (model) this.selectBase(model);
  }

  protected mobileSetStem(value: Stem | string): void {
    if (this.selectedBase) this.pickStem(this.selectedBase, value);
  }

  protected mobileSetWheels(value: boolean): void {
    if (this.selectedBase) this.pickWheels(this.selectedBase, value);
  }

  protected mobileSetCover(value: boolean): void {
    if (this.selectedBase) this.pickCover(this.selectedBase, value);
  }

  /* ------------------------- option availability ------------------------- */

  /**
   * `attributes` is optional on a catalog base, so it is never reached through
   * in the template: an undefined dereference there throws out of the render
   * and blanks the whole page.
   */
  protected hasWheels(model: BaseModel): boolean {
    if (!model.attributes || !model.attributes.wheels) return false;
    // Shade Designer's one exception, kept so both apps offer the same thing.
    return !(
      this.handle === ProductLineName.Eclipse && model.model === "36G-SQx3"
    );
  }

  /**
   * A cover is offered only when the attribute AND the accessory agree.
   * Several Eclipse bases set the flag with no -TC accessory behind it, and a
   * cover there has no model and no price.
   */
  protected hasCover(model: BaseModel): boolean {
    if (!model.attributes || !model.attributes.topCover) return false;
    return !!this.coverAccessory(model);
  }

  protected coverAccessory(model: BaseModel): BaseAccessory | null {
    const accessories = model.accessories || [];
    const match = accessories.find(
      accessory => !!accessory.model && accessory.model.endsWith("-TC")
    );
    return match || null;
  }

  /** Mounts mapped onto the Stem enum. Entries that don't map are dropped. */
  protected mountStems(model: BaseModel): Stem[] {
    const isMarket = this.umbrellaType === UmbrellaType.Market;
    const stems: Stem[] = [];
    (model.mounts || []).forEach(mount => {
      if (!mount || !mount.model) return;
      // Market umbrellas take no 2" stem.
      if (isMarket && mount.model.indexOf("2") > -1) return;
      const stem = this.stemFromMount(mount.model);
      if (stem && stems.indexOf(stem) === -1) stems.push(stem);
    });
    return stems.sort(
      (a, b) => STEM_ORDER.indexOf(a) - STEM_ORDER.indexOf(b)
    );
  }

  /**
   * What the stem row renders. A preselected "Spigot" is an attachment style
   * rather than a stem length, so it is shown alongside the stems only when
   * the design actually asked for it.
   */
/**
   * Only the attachments this umbrella can actually take.
   *
   * A cantilever hangs its canopy off an arm, so its base mounts by spigot and
   * never takes a stem. Every shared cantilever design confirms it: Eclipse and
   * Aurora codes all resolve with `baseStem: "Spigot"`.
   *
   * Shade Designer reaches the same place by a different route, which is worth
   * knowing before changing this. It has no spigot option on the base step at
   * all — spigot is a *frame* choice there (SpigotGroup.vue, a Standard vs
   * Stainless finish, gated on `handle === Eclipse || handle === Aurora`), and
   * its base step simply renders `model.mounts`. Cantilever bases come back
   * from `?bases` with `mounts: []`, so the stem row never appears for them.
   * The absence is emergent from the data rather than a stated rule.
   *
   * This app cannot rely on that: it hydrates from the grouped catalog, where
   * those same bases DO carry mounts, and Shade Quote models the spigot as the
   * base's stem rather than a frame option — which is what the resolve
   * endpoint hands over. So the rule is stated explicitly here.
   *
   * `umbrella_type === cantilever` is exactly {Eclipse, Aurora}, matching
   * Shade Designer's hardcoded pair. Nova is added because this app's own base
   * page already treats it as spigot-capable — CustomizeBase.needsSpigotOption
   * special-cases it by name — and a base configured here has to stay
   * consistent with the page a rep reopens it on.
   */
  protected get spigotOnly(): boolean {
    return (
      this.umbrellaType === UmbrellaType.Cantilever ||
      this.handle === ProductLineName.Nova
    );
  }

  protected stemOptions(model: BaseModel): (Stem | typeof SPIGOT_STEM)[] {
    if (this.spigotOnly) return [SPIGOT_STEM];
    return this.mountStems(model);
  }

  /**
   * What the stem row actually draws — nothing at all on a spigot-only line.
   *
   * BugHerd #2: a row holding one permanently-selected button, on every base,
   * for every cantilever, is a control that cannot be operated. The fact it
   * conveys is true and worth saying, so it moves to a note under the section
   * title (the Canopy card sets the precedent) and is said once instead of
   * once per tile.
   *
   * `stemOptions` is deliberately left alone: the selection still resolves to
   * "Spigot", the line still carries it, and the production note still reaches
   * NetSuite and the client PDF. Only the control disappears.
   */
  protected renderedStems(model: BaseModel): (Stem | typeof SPIGOT_STEM)[] {
    return this.spigotOnly ? [] : this.stemOptions(model);
  }

  private stemFromMount(mountModel: string): Stem | null {
    return (Object.values(Stem) as string[]).indexOf(mountModel) > -1
      ? (mountModel as Stem)
      : null;
  }

  /**
   * Stem labels carry their model code.
   *
   * Shade Designer names both `18ST` and `18ST2` "18\" Stem", and the catalog
   * gives nothing to tell them apart — identical `display_name`, empty
   * attributes — so mirroring it produced two adjacent, identical, separately
   * clickable buttons that pick different SKUs (`-18ST` vs `-18ST2`). A rep
   * could not see which one they had chosen.
   *
   * The code is appended rather than replacing the friendly name: CustomizeBase
   * already labels these options by raw code, so a rep who opens the base line
   * later sees the same vocabulary.
   */
  protected stemLabel(stem: Stem | string): string {
    return stem === SPIGOT_STEM ? "Spigot" : String(stem);
  }

  /**
   * The production note this stem choice carries, shown beside the row.
   *
   * CustomizeBase surfaces the same strings, but only inside its stem Note
   * modal — a box this card does not have. Without them a rep picking Spigot
   * sees nothing explaining that no stem is being ordered, which is exactly
   * what the note tells the factory. `baseStemNotes` is the single source, so
   * what is displayed here and what is written to the line cannot drift.
   */
  protected get stemNote(): string {
    if (!this.selectedBase || !this.baseStem) return "";
    // On a spigot-only line the same fact is stated once under the section
    // title instead of under every tile (BugHerd #2). The note still travels
    // on the line either way — baseStemNotes is what writes it.
    if (this.spigotOnly) return "";
    const notes = baseStemNotes({
      base: this.selectedBase,
      baseFinish: (this.baseFinish ?? {}) as Finish,
      baseStem: this.baseStem,
      baseOptions: {}
    });
    return notes.length ? notes[0].content : "";
  }

  /**
   * Said once, under the section title, in place of a stem row nobody can
   * operate — BugHerd #2. It explains what is happening to a rep who would
   * otherwise see wheels and cover options on a base and wonder why it alone
   * offers no stem.
   */
  protected get spigotSectionNote(): string {
    return (
      "Instead of a stem, a spigot attachment will be used when pairing " +
      "your Cantilever umbrella with a base."
    );
  }

  protected isStemActive(model: BaseModel, stem: Stem | string): boolean {
    return this.isSelected(model) && this.baseStem === stem;
  }

  protected isWheelsActive(model: BaseModel, value: boolean): boolean {
    return this.isSelected(model) && this.wheels === value;
  }

  protected isCoverActive(model: BaseModel, value: boolean): boolean {
    return this.isSelected(model) && this.coverOn === value;
  }

  /* ------------------------------ finishes ------------------------------ */

  protected ownFinishes(model: BaseModel): Finish[] {
    return this.orderFinishes(model.finishes);
  }

  protected get coverFinishes(): Finish[] {
    const base = this.selectedBase;
    const accessory = base ? this.coverAccessory(base) : null;
    return this.orderFinishes(accessory ? accessory.finishes : []);
  }

  private orderFinishes(finishes?: Finish[]): Finish[] {
    const available = finishes || [];
    const ordered: Finish[] = [];
    FINISH_ORDER.forEach(handle => {
      const match = available.find(
        finish => String(finish.handle) === handle
      );
      if (match) ordered.push(match);
    });
    // Anything the enum doesn't know about still has to be selectable.
    available.forEach(finish => {
      if (ordered.indexOf(finish) === -1) ordered.push(finish);
    });
    return ordered;
  }

  private validCoverFinish(
    accessory: BaseAccessory,
    wanted: Finish | null
  ): Finish {
    const finishes = this.orderFinishes(accessory.finishes);
    if (!finishes.length) return this.coverFinish;
    const carried = wanted
      ? finishes.find(finish => String(finish.handle) === String(wanted.handle))
      : undefined;
    const golden = finishes.find(
      finish => String(finish.handle) === String(FinishHandle.WG)
    );
    return carried || golden || finishes[0];
  }

  protected isBaseFinishActive(model: BaseModel, finish: Finish): boolean {
    return (
      this.isSelected(model) &&
      !!this.baseFinish &&
      String(this.baseFinish.handle) === String(finish.handle)
    );
  }

  protected isCoverFinishActive(finish: Finish): boolean {
    return String(this.coverFinish.handle) === String(finish.handle);
  }

  /** e.g. "36G-SQ-TC-BZ" */
  protected get coverFinishHeading(): string {
    const base = this.selectedBase;
    const accessory = base ? this.coverAccessory(base) : null;
    if (!accessory) return "";
    return `${accessory.model}-${String(
      this.coverFinish.handle
    ).toUpperCase()}`;
  }

  protected swatchStyle(handle: FinishHandle): string {
    const image = this.swatchImage(handle);
    return image ? `background-image: url(${image});` : "";
  }

  /**
   * The base/cover swatch set, not the frame finish one — BugHerd #3. See
   * getBaseFinishImage for why they differ.
   *
   * It `require()`s by handle, so a handle with no image on disk throws; gbk
   * is the one in the catalog today, and an empty swatch is what Shade
   * Designer renders for it.
   */
  private swatchImage(handle: FinishHandle): string {
    if (String(handle) === String(FinishHandle.GBK)) return "";
    try {
      return getBaseFinishImage(handle);
    } catch (err) {
      return "";
    }
  }

  /* ------------------------------ pricing ------------------------------ */

  private cancelPendingPricing(): void {
    if (this.priceTimer !== null) {
      window.clearTimeout(this.priceTimer);
      this.priceTimer = null;
    }
  }

  private scheduleRefreshPricing(): void {
    if (this.pricingSuspended) return;
    this.cancelPendingPricing();
    // A reprice is now owed for a newer config, so any reply still in flight
    // is stale — drop it, or it would emit the new selection with the old
    // price. Most likely in the umbrella page's setup editor, where the seeded
    // base is still being priced as the rep starts clicking.
    this.priceSeq++;
    this.priceTimer = window.setTimeout(() => {
      this.priceTimer = null;
      this.refreshPricing();
    }, PRICE_DEBOUNCE_MS);
  }

  /**
   * The single entry point for repricing, after CustomizeBase's pattern.
   * /prices/base 500s on a payload with no `model` and the resulting toast
   * sticks until it is dismissed, so a config that cannot be priced is left
   * alone: it is a not-yet state, not an error.
   */
  private async refreshPricing(forPreselect = false): Promise<void> {
    const base = this.selectedBase;
    if (!base || !base.model) return;
    const seq = ++this.priceSeq;
    if (forPreselect) this.preselectSeq = seq;
    const accessory = this.coverOn ? this.coverAccessory(base) : null;
    const coverHandle = this.coverFinish.handle;
    this.pricingRequests++;
    try {
      const [basePrice, coverPrice] = await Promise.all([
        this.fetchBasePrice(base),
        accessory
          ? this.fetchCoverPrice(accessory.model, coverHandle)
          : Promise.resolve(0)
      ]);
      // Last request wins: an earlier reply arriving late must not overwrite a
      // newer one, and must not emit a price for a config the rep has left.
      if (seq !== this.priceSeq) return;
      this.basePrice = basePrice;
      this.coverPrice = coverPrice;
      this.emitChange(seq === this.preselectSeq);
    } finally {
      this.pricingRequests--;
    }
  }

  private async fetchBasePrice(base: BaseModel): Promise<number> {
    try {
      const price = await this.productService.getBasePrice({
        model: base.model,
        item_details: {
          base,
          baseFinish: (this.baseFinish
            ? this.baseFinish.handle
            : null) as FinishHandle,
          // "Spigot" is not a Stem. It stays on the selection but must be
          // nulled here.
          baseStem:
            this.baseStem === SPIGOT_STEM
              ? null
              : (this.baseStem as Stem | null),
          // Deliberately only `wheels`: /prices/base reads `finishes` off a
          // `topCover` key and 500s on it. The cover is priced separately,
          // below, and would double-count here anyway.
          baseOptions: { wheels: this.wheels }
        }
      });
      return price.base || 0;
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
        return 0;
      }
      NotificationService.show(
        `Unable to price base ${base.model}. Please try again.`,
        "warning",
        12000
      );
      return 0;
    }
  }

  /**
   * A top cover's msrp is 0 — the finish upcharge IS its price. Cached per
   * cover model, since several bases share one.
   */
  private async fetchCoverPrice(
    coverModel: string,
    handle: FinishHandle
  ): Promise<number> {
    let finishes = this.coverPrices[coverModel];
    if (!finishes) {
      try {
        const res = (await this.productService.getSingleProduct(
          coverModel
        )) as SingleProductRes;
        const price = res && res.product ? res.product.price : null;
        finishes = (price && price.finishes) || [];
        this.coverPrices[coverModel] = finishes;
      } catch (err) {
        NotificationService.show(
          `Unable to price the ${coverModel} top cover. Please check its ` +
            "price in the quote.",
          "warning",
          12000
        );
        return 0;
      }
    }
    const match = finishes.find(
      finish => String(finish.handle) === String(handle)
    );
    return match ? match.upcharge : 0;
  }

  /**
   * Emitted once per settled change, so the parent never holds a selection
   * paired with another config's price.
   */
  private emitChange(fromPreselect = false): void {
    const selection = this.currentSelection;
    const change: BaseSelectionChange = {
      selection,
      basePrice: selection ? this.basePrice : 0,
      coverPrice:
        selection && selection.baseOptions.topCover ? this.coverPrice : 0,
      ...(fromPreselect ? { fromPreselect: true } : {})
    };
    this.$emit("change", change);
  }

  protected get mobileBaseOptions(): BaseModel[] {
    const options = [
      // display_model is what the dropdown row renders, so the sentinel's
      // model code never reaches the screen.
      ({
        model: NO_BASE,
        display_model: "No Base",
        display_name: "Umbrella only"
      } as unknown) as BaseModel
    ];
    this.groups.forEach(group => {
      group.models.forEach(model => options.push(model));
    });
    return options;
  }
}
